import * as React from 'react';

import cx from 'classnames';

import { Link } from '~shared/components/Link';
import { useId } from '~shared/hooks';

import { GO_TO_ITEM, REGISTER_ITEM, UNREGISTER_ITEM } from './actions';
import { RenderProps } from './hooks/useMenuItem';
import { MenuItemDataRef, useMenuContext } from './MenuContext';
import { disposables } from './utils/disposables';
import { Focus } from './utils/getIndex';

interface LinkRenderProps extends RenderProps {
  href: string;
}

export interface MenuItemLinkRenderPropArgs {
  isActive: boolean;
  props?: LinkRenderProps;
}

interface Props {
  children: ({
    isActive,
    props,
  }: MenuItemLinkRenderPropArgs) => React.ReactElement;
  disabled?: boolean;
  href: string;
  target?: string;
}

/**
 * Similar to MenuItem, except intended to be used with anchor elements, rather
 * than buttons. Does not include `onClick` in the render props, but does
 * include the `href` prop passed to it.
 *
 * @see MenuItem
 */
export function MenuItemLink(props: Props) {
  const { children, disabled = false, ...passthroughProps } = props;
  const [state, dispatch] = useMenuContext('MenuItem');
  const id = `menu-item-${useId()}`;
  const isActive =
    state.activeItemIndex !== null
      ? state.items[state.activeItemIndex].id === id
      : false;

  React.useEffect(() => {
    if (!state.isOpen) return;
    if (!isActive) return;

    const d = disposables();

    d.nextFrame(() =>
      document.getElementById(id)?.scrollIntoView?.({ block: 'nearest' })
    );

    return d.dispose;
  }, [state.isOpen, isActive, id]);

  const ref = React.useRef<MenuItemDataRef['current']>({ disabled });

  /** Add disabled state to item data. */
  React.useEffect(() => {
    ref.current.disabled = disabled;
  }, [ref, disabled]);

  /** Add text content to item data. */
  React.useEffect(() => {
    ref.current.text = document.getElementById(id)?.textContent?.toLowerCase();
  }, [ref, id]);

  /** Add (or remove) item data to state items list. */
  React.useEffect(() => {
    dispatch({ type: REGISTER_ITEM, id, dataRef: ref });
    return () => dispatch({ type: UNREGISTER_ITEM, id });
  }, [dispatch, ref, id]);

  const handleFocus = React.useCallback(() => {
    if (disabled) return dispatch({ type: GO_TO_ITEM, focus: Focus.NOTHING });
    dispatch({ type: GO_TO_ITEM, focus: Focus.SPECIFIC, id });
  }, [disabled, dispatch, id]);

  const handleMove = React.useCallback(() => {
    if (disabled) return;
    if (isActive) return;
    dispatch({ type: GO_TO_ITEM, focus: Focus.SPECIFIC, id });
  }, [disabled, isActive, dispatch, id]);

  const handleLeave = React.useCallback(() => {
    if (disabled) return;
    if (!isActive) return;
    dispatch({ type: GO_TO_ITEM, focus: Focus.NOTHING });
  }, [disabled, isActive, dispatch]);

  const itemProps = {
    id,
    role: 'menuitem',
    tabIndex: disabled === true ? undefined : -1,
    'aria-disabled': disabled === true ? true : undefined,
    disabled: undefined,
    // onClick: handleClick,
    onFocus: handleFocus,
    onPointerMove: handleMove,
    onMouseMove: handleMove,
    onPointerLeave: handleLeave,
    onMouseLeave: handleLeave,
  };

  return children({
    isActive,
    props: { ...passthroughProps, ...itemProps },
  });
}

type StyledProps = Omit<Props, 'children'> & {
  children: React.ReactNode;
};

export function StyledMenuLink({ children, ...restProps }: StyledProps) {
  const getLinkClass = (isActive = false, isDisabled = false) =>
    cx(
      'flex w-full px-4 py-2',
      'font-sans text-base leading-8 text-left rounded-1 no-underline',
      isActive ? 'bg-cyan-50' : 'bg-white',
      isDisabled ? 'text-grey-600' : 'text-blue-900'
    );

  return (
    <MenuItemLink {...restProps}>
      {({ isActive, props }) => (
        <div className="px-2 py-1">
          <Link
            className={getLinkClass(isActive, props['aria-disabled'])}
            {...props}
          >
            {children}
          </Link>
        </div>
      )}
    </MenuItemLink>
  );
}
